import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getQuizTaking, submitQuiz, type SubmitResult } from "@/api/quiz";
import { pickTitle } from "@/api/content";
import { Badge, Button } from "@/components";
import { useLang } from "@/i18n/LanguageContext";

/** Knowledge-check panel shown on a gated program step (employee, interactive). */
export function QuizPanel({ stepId, onSubmitted }: { stepId: string; onSubmitted: () => void }) {
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [result, setResult] = useState<SubmitResult | null>(null);

  const quiz = useQuery({ queryKey: ["quiz-take", stepId], queryFn: ({ signal }) => getQuizTaking(stepId, signal), enabled: open });

  const submit = useMutation({
    mutationFn: () => submitQuiz(stepId, answers),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ["quiz-take", stepId] });
      onSubmitted();
    },
  });

  function setSingle(qid: string, optId: string) {
    setAnswers((a) => ({ ...a, [qid]: [optId] }));
  }
  function toggleMulti(qid: string, optId: string) {
    setAnswers((a) => {
      const cur = a[qid] ?? [];
      return { ...a, [qid]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
    });
  }

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}>{t("Maak de kennischeck", "Take the knowledge check")}</Button>
    );
  }

  return (
    <div style={{ background: "var(--bg)", borderRadius: 8, padding: 16 }}>
      {quiz.isLoading || !quiz.data ? (
        <p style={{ color: "var(--grey-text)" }}>{t("Laden…", "Loading…")}</p>
      ) : (
        <>
          {quiz.data.questions.map((q, i) => (
            <div key={q.id} style={{ marginBottom: 14 }}>
              <p style={{ fontWeight: 600, marginBottom: 6 }}>{i + 1}. {pickTitle(q.prompt_i18n, lang, q.prompt)}</p>
              {q.options.map((o) => (
                <label key={o.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <input
                    type={q.kind === "single" ? "radio" : "checkbox"}
                    name={q.id}
                    checked={(answers[q.id] ?? []).includes(o.id)}
                    onChange={() => (q.kind === "single" ? setSingle(q.id, o.id) : toggleMulti(q.id, o.id))}
                  />
                  {pickTitle(o.text_i18n, lang, o.text)}
                </label>
              ))}
            </div>
          ))}
          <div className="toolbar">
            <Button onClick={() => submit.mutate()} disabled={submit.isPending}>{submit.isPending ? t("Nakijken…", "Grading…") : t("Inleveren", "Submit")}</Button>
            {result && (
              <Badge tone={result.passed ? "ok" : "err"}>
                {result.passed ? t("Geslaagd", "Passed") : t("Niet gehaald", "Not passed")} — {result.score}% ({t("drempel", "threshold")} {result.pass_threshold}%)
              </Badge>
            )}
          </div>
        </>
      )}
    </div>
  );
}
